import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Clock3, MapPin, CheckCircle2, ArrowRight, Truck, Zap, Key, Wrench, LifeBuoy, Cog } from "lucide-react";
import Layout from "@/components/site/Layout";
import Seo from "@/components/site/Seo";
import HeroQuoteForm from "@/components/site/HeroQuoteForm";
import ShareLocationButton from "@/components/site/ShareLocationButton";
import FaqBlock from "@/components/site/FaqBlock";
import { PHONE_DISPLAY, PHONE_TEL, TID } from "@/config/site";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

const KIRKLAND_SERVICES = [
  { icon: Truck, title: "24/7 Emergency Towing", desc: "Freeway shoulder recoveries and after-hours calls answered by a real person.", path: "/emergency-towing" },
  { icon: Truck, title: "Flatbed Towing", desc: "Zero drivetrain contact — safest option for AWD, luxury, and low-clearance cars.", path: "/flatbed-towing" },
  { icon: LifeBuoy, title: "Roadside Assistance", desc: "Fast fix for dead batteries, flats, and stalls anywhere in Kirkland.", path: "/roadside-assistance" },
  { icon: Zap, title: "Jump Start", desc: "Cold garage or driveway battery? Portable jump packs on every truck.", path: "/jump-start" },
  { icon: Key, title: "Car Lockout", desc: "Locked out at the marina or a downtown lot? Non-destructive entry.", path: "/car-lockout" },
  { icon: Wrench, title: "Tire Change", desc: "Spare-on assist or short tow to the nearest tire shop.", path: "/tire-change" },
  { icon: ShieldCheck, title: "Accident Recovery & Winch-out", desc: "Post-collision and off-shoulder recovery, coordinated with police and insurance.", path: "/winch-out-service" },
  { icon: Cog, title: "Vehicle & Equipment Transport", desc: "Local and long-distance flatbed transport for dealerships, shops, and private buyers.", path: "/equipment-transportation" },
];

const KIRKLAND_FAQS = [
  {
    q: "Do you provide 24/7 towing service in Kirkland?",
    a: "Yes. BM Towing & Recovery answers the phone 24 hours a day, every day of the year — nights, weekends, and holidays included. Call (425) 647-9585 and a real person picks up.",
  },
  {
    q: "How quickly can a tow truck reach me in Kirkland?",
    a: "Kirkland is our home base, so response times here are typically our fastest anywhere on the Eastside. Traffic and time of day still matter — we'll give you an honest ETA on the phone before we dispatch.",
  },
  {
    q: "Do you offer flatbed towing in Kirkland?",
    a: "Yes. Flatbed is the safest option for AWD/4WD vehicles, luxury cars, and anything low-clearance. We flatbed-tow throughout Kirkland — from Downtown and the Marina to Totem Lake, Juanita, and Rose Hill.",
  },
  {
    q: "Can you help with roadside assistance — jump starts, lockouts, and flat tires — in Kirkland?",
    a: "Absolutely. Roadside is a big part of what we do. Dead batteries in Bridle Trails garages, lockouts in Downtown Kirkland parking lots, flats on I-405 exits, and tire changes on residential streets — all handled 24/7.",
  },
  {
    q: "Do you handle accident recovery in Kirkland?",
    a: "Yes. We work post-collision tows across Kirkland, coordinate with local police at the scene, and can transport the vehicle directly to your preferred body shop or insurance yard.",
  },
  {
    q: "What areas of Kirkland do you cover?",
    a: "All of them — Downtown Kirkland, the Marina, Juanita, Totem Lake, Rose Hill, Bridle Trails, Finn Hill, Kingsgate, and Highlands. We also handle the I-405 and SR-520 corridors that run through Kirkland.",
  },
  {
    q: "Is BM Towing & Recovery licensed and insured?",
    a: "Yes — fully licensed and insured to operate in Washington state. Trained flatbed operators, maintained trucks, and honest pricing quoted on the phone before we roll.",
  },
];

export default function KirklandTowingPage() {
  const url = typeof window !== "undefined" ? window.location.href : "/kirkland-towing";

  // JSON-LD: LocalBusiness (Kirkland-focused), Service catalog, FAQPage, Breadcrumbs.
  const localSchema = {
    ...localBusinessSchema({ url }),
    description:
      "24/7 towing service in Kirkland, WA — flatbed, emergency towing, roadside assistance, jump starts, lockouts, tire changes, accident recovery, and vehicle transport.",
    areaServed: { "@type": "City", name: "Kirkland, WA" },
  };
  const serviceCatalogSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Towing Service",
    name: "24/7 Towing Service in Kirkland, WA",
    provider: localBusinessSchema({ url }),
    areaServed: { "@type": "City", name: "Kirkland, WA" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Towing & Roadside Services in Kirkland",
      itemListElement: KIRKLAND_SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title },
      })),
    },
    url,
  };
  const schemas = [
    localSchema,
    serviceCatalogSchema,
    faqSchema(KIRKLAND_FAQS),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Towing Service in Kirkland, WA", url: "/kirkland-towing" },
    ]),
  ].filter(Boolean);

  return (
    <Layout>
      <Seo
        title="24/7 Towing Service in Kirkland, WA"
        description="BM Towing & Recovery — 24/7 towing service in Kirkland, WA. Flatbed towing, emergency towing, roadside assistance, jump starts, lockouts, tire changes, accident recovery, and vehicle transport. Call (425) 647-9585 anytime."
        canonical={url}
        jsonLd={schemas}
        keywords="towing Kirkland, tow truck Kirkland, 24/7 towing Kirkland WA, flatbed towing Kirkland, roadside assistance Kirkland"
      />

      {/* Hero */}
      <section
        className="relative isolate overflow-hidden bg-black border-b border-yellow-400/30"
        data-testid="kirkland-hero"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40"
          style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 60%)" }}
        />
        <div className="pointer-events-none absolute inset-0 bm-grain" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <nav className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-zinc-500 mb-4">
              <Link to="/" className="hover:text-yellow-400">Home</Link>
              <span>/</span>
              <span className="text-yellow-400">Kirkland Towing</span>
            </nav>

            <div className="inline-flex items-center gap-2 bg-yellow-400 text-slate-900 px-3 py-1 font-display font-black uppercase tracking-widest text-xs border-2 border-black">
              <MapPin className="h-3 w-3" strokeWidth={3} /> Kirkland, WA · Open 24/7
            </div>

            <h1
              data-testid="kirkland-h1"
              className="mt-6 font-display font-black uppercase text-white leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
            >
              24/7 Towing Service in <span className="text-yellow-400">Kirkland, WA</span>
            </h1>

            <p className="mt-5 text-zinc-300 text-lg max-w-2xl leading-relaxed">
              BM Towing &amp; Recovery is a Kirkland-based, 24/7 towing and roadside company. From a dead battery in Juanita to a flatbed pickup at a Downtown Kirkland dealership, one phone call puts a professional driver on your route — day or night.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="kirkland-call-hero"
                aria-label={`Call BM Towing 24/7 at ${PHONE_DISPLAY}`}
                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-4 text-base border-2 border-black bm-hard-shadow min-h-[56px]"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> Call Now: {PHONE_DISPLAY}
              </a>
              <ShareLocationButton
                testId="kirkland-share-location"
                variant="ghost"
                label="Share My Location"
                className="text-base"
              />
              <a
                href="#kirkland-quote"
                data-testid="kirkland-quote-scroll"
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white border-2 border-white/30 hover:border-yellow-400/60 font-display font-black uppercase tracking-wider px-6 py-4 text-base transition-colors min-h-[56px]"
              >
                Request Service <ArrowRight className="h-4 w-4" strokeWidth={3} />
              </a>
            </div>

            <ul className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl">
              <li className="lux-glass rounded-xl p-3 flex items-start gap-2">
                <Clock3 className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-display font-black uppercase text-white text-xs">24/7 Response</div>
                  <div className="text-zinc-400 text-[11px]">Nights · weekends · holidays</div>
                </div>
              </li>
              <li className="lux-glass rounded-xl p-3 flex items-start gap-2">
                <ShieldCheck className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-display font-black uppercase text-white text-xs">Licensed &amp; Insured</div>
                  <div className="text-zinc-400 text-[11px]">Trained flatbed operators</div>
                </div>
              </li>
              <li className="lux-glass rounded-xl p-3 flex items-start gap-2">
                <MapPin className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-display font-black uppercase text-white text-xs">Kirkland Based</div>
                  <div className="text-zinc-400 text-[11px]">Fastest local ETAs</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Right: quote form */}
          <div id="kirkland-quote" className="lg:col-span-5 lg:justify-self-end w-full">
            <div className="lux-glass-strong rounded-2xl border-yellow-400/40 p-5 sm:p-6">
              <HeroQuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Local context */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Local to Kirkland</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
            Kirkland is where we live, work, and dispatch from.
          </h2>
          <p className="mt-5 text-zinc-300 text-base sm:text-lg leading-relaxed">
            Because we&apos;re based in Kirkland, we already know the routes — every shortcut through Juanita, every I-405 exit into Downtown, and every tight garage in Totem Lake. When your car quits on Lake Washington Boulevard or you&apos;re locked out at Kirkland Urban, our closest truck is usually minutes away.
          </p>
          <p className="mt-4 text-zinc-400 text-base leading-relaxed">
            We operate as a mobile 24/7 service — no shop queue, no yard wait. You call, we quote an honest price on the phone, and a driver rolls. That&apos;s it. Kirkland has been our home base for years, and it&apos;s where we handle more calls than anywhere else on the Eastside.
          </p>
        </div>
      </section>

      {/* Services grid with internal links */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Kirkland Services</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl leading-tight max-w-3xl">
            Every service you might need in Kirkland — one number.
          </h2>
          <p className="mt-4 text-zinc-400 text-base max-w-2xl">
            Tap any service for full details, pricing notes, and the situations we handle most.
          </p>

          <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {KIRKLAND_SERVICES.map((s) => (
              <li key={s.title}>
                <Link
                  to={s.path}
                  data-testid={`kirkland-service-${s.path.replace("/", "")}`}
                  className="group h-full flex flex-col bg-black border-2 border-white/10 hover:border-yellow-400 p-5 transition-colors"
                >
                  <s.icon className="h-6 w-6 text-yellow-400" strokeWidth={2.5} aria-hidden="true" />
                  <div className="mt-3 font-display font-black uppercase text-white text-base leading-tight">
                    {s.title}
                  </div>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-yellow-400 font-display uppercase tracking-widest text-[11px]">
                    View details
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" strokeWidth={2.5} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Trust / why call us */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Why Call Us</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
            Honest pricing. Real dispatchers. Local drivers.
          </h2>
          <ul className="mt-6 space-y-4">
            {[
              "A real person answers the phone — no menus, no bots, no queues.",
              "Honest ETA and flat quote given before we dispatch a truck.",
              "Flatbed-first policy for AWD, luxury, and low-clearance vehicles.",
              "Trained operators, maintained equipment, fully licensed and insured.",
              "Locally based in Kirkland — usually the closest truck to your Kirkland address.",
              "Post-collision recovery: we coordinate with police and your insurance.",
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 text-zinc-300 text-base leading-relaxed">
                <CheckCircle2 className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="kirkland-call-mid"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-4 text-base border-2 border-black bm-hard-shadow min-h-[56px]"
            >
              <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">FAQ</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
            Towing in Kirkland — common questions
          </h2>
          <div className="mt-6">
            <FaqBlock faqs={KIRKLAND_FAQS} testId="kirkland-faq" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Stranded in Kirkland?</div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl leading-tight">
            One call. Truck rolling. <span className="text-yellow-400">24/7.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-lg max-w-xl mx-auto">
            Real person answers. Honest quote on the phone. Kirkland-based dispatch.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid={TID.heroCall}
              aria-label={`Call BM Towing 24/7 at ${PHONE_DISPLAY}`}
              className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-4 text-base border-2 border-black bm-hard-shadow min-h-[56px]"
            >
              <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
            </a>
            <ShareLocationButton
              testId="kirkland-share-bottom"
              variant="ghost"
              label="Share My Location"
              className="text-base"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}
